export const SORT_CRITERIA = {
  RECENT: "recent",
  OLDEST: "oldest",
  AZ: "az",
  ZA: "za",
};

export const SORT_OPTIONS = [
  { value: SORT_CRITERIA.RECENT, label: "Más recientes" },
  { value: SORT_CRITERIA.OLDEST, label: "Más antiguos" },
  { value: SORT_CRITERIA.AZ, label: "A → Z" },
  { value: SORT_CRITERIA.ZA, label: "Z → A" },
];

const compareText = (left, right) =>
  String(left || "").localeCompare(String(right || ""), "es-MX", {
    sensitivity: "base",
    numeric: true,
  });

const getAlphabeticalLabel = (value) =>
  String(value || "")
    .replace(/^\s*\d+\s*[.)\-:]\s*/, "")
    .trim();

const parseTimeOffset = (timeValue) => {
  const match = String(timeValue || "").match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);

  if (!match) {
    return 0;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] || 0);

  return ((hours * 60 + minutes) * 60 + seconds) * 1000;
};

const toTimestamp = (dateValue, timeValue = "") => {
  if (!dateValue) {
    return null;
  }

  const dateText = String(dateValue).trim();
  const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateText);
  const parsed = Date.parse(isoDateOnly ? `${dateText}T00:00:00` : dateText);

  if (Number.isNaN(parsed)) {
    return null;
  }

  return parsed + parseTimeOffset(timeValue);
};

const compareByDate = (leftTimestamp, rightTimestamp, descending) => {
  const leftMissing = leftTimestamp === null;
  const rightMissing = rightTimestamp === null;

  if (leftMissing && rightMissing) {
    return 0;
  }

  if (leftMissing) {
    return 1;
  }

  if (rightMissing) {
    return -1;
  }

  return descending
    ? rightTimestamp - leftTimestamp
    : leftTimestamp - rightTimestamp;
};

const compareIds = (left, right, descending) => {
  const leftId = Number(left) || 0;
  const rightId = Number(right) || 0;
  return descending ? rightId - leftId : leftId - rightId;
};

const compareAlphabetically = (left, right, criterion) => {
  const leftLabel = getAlphabeticalLabel(left?.nombre_clase);
  const rightLabel = getAlphabeticalLabel(right?.nombre_clase);
  const nameComparison = compareText(leftLabel, rightLabel);

  if (nameComparison !== 0) {
    return criterion === SORT_CRITERIA.AZ ? nameComparison : -nameComparison;
  }

  const classComparison = compareText(left?.matricula_clase, right?.matricula_clase);
  if (classComparison !== 0) {
    return criterion === SORT_CRITERIA.AZ ? classComparison : -classComparison;
  }

  return 0;
};

export const sortCourses = (courses, criterion = SORT_CRITERIA.RECENT) => {
  const sorted = [...(Array.isArray(courses) ? courses : [])];

  return sorted.sort((left, right) => {
    if (criterion === SORT_CRITERIA.AZ || criterion === SORT_CRITERIA.ZA) {
      const alphabeticalComparison = compareAlphabetically(left, right, criterion);
      if (alphabeticalComparison !== 0) {
        return alphabeticalComparison;
      }
      return compareIds(left?.id_clase, right?.id_clase, true);
    }

    const descending = criterion !== SORT_CRITERIA.OLDEST;
    const dateComparison = compareByDate(
      toTimestamp(left?.fecha_inicio),
      toTimestamp(right?.fecha_inicio),
      descending,
    );

    if (dateComparison !== 0) {
      return dateComparison;
    }

    return compareIds(left?.id_clase, right?.id_clase, descending);
  });
};

export const sortZoomLinks = (links, criterion = SORT_CRITERIA.RECENT) => {
  const sorted = [...(Array.isArray(links) ? links : [])];

  return sorted.sort((left, right) => {
    if (criterion === SORT_CRITERIA.AZ || criterion === SORT_CRITERIA.ZA) {
      const alphabeticalComparison = compareAlphabetically(left, right, criterion);
      if (alphabeticalComparison !== 0) {
        return alphabeticalComparison;
      }
      return compareIds(left?.id_url, right?.id_url, true);
    }

    const descending = criterion !== SORT_CRITERIA.OLDEST;
    const dateComparison = compareByDate(
      toTimestamp(left?.fecha, left?.hora),
      toTimestamp(right?.fecha, right?.hora),
      descending,
    );

    if (dateComparison !== 0) {
      return dateComparison;
    }

    return compareIds(left?.id_url, right?.id_url, descending);
  });
};
